import { CUSTOMER_ORDER_STATUS_LABELS, type CustomerOrderStatus, type OrdersChatSummaryDTO } from '@bifeai/shared';
import type { Tone } from '@bifeai/ui';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatKilos } from '../../lib/format';

export const ORDER_TONES: Record<CustomerOrderStatus, Tone> = {
  NEW: 'info',
  PREPARING: 'warning',
  READY: 'success',
  DELIVERED: 'neutral',
  CANCELED: 'danger',
};
export const orderLabel = (s: CustomerOrderStatus) => CUSTOMER_ORDER_STATUS_LABELS[s];

/** 2000 g → "2 kg"; 3 u. → "3 u." */
export const qtyText = (unitType: 'WEIGHT' | 'UNIT', qty: number) => (unitType === 'WEIGHT' ? formatKilos(qty) : `${qty} u.`);

/** Números de la campanita de pedidos (también los usa el menú). */
export function useOrdersSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['orders-chat', 'summary'],
    queryFn: () => api.get<OrdersChatSummaryDTO>('/orders-chat/summary'),
    enabled,
    refetchInterval: 15_000,
  });
}

/** Teléfono de WhatsApp legible: "5491144781203" → "11 4478-1203"; otros números de Argentina con su característica; el resto con +. */
export function phoneText(wa: string): string {
  const d = wa.replace(/\D/g, '');
  if (d.startsWith('54911') && d.length === 13) return `11 ${d.slice(5, 9)}-${d.slice(9)}`;
  if (d.startsWith('549') && d.length === 13) return `${d.slice(3, 6)} ${d.slice(6)}`;
  return `+${d}`;
}

/** Iniciales para el avatar de la bandeja. */
export const initials = (name: string) =>
  name
    .replace(/[^\p{L}\s]/gu, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('') || '#';

/** Hora corta si es de hoy; si no, día y mes. */
export function shortTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

/** "sáb 24/10 10:00" */
export function whenText(iso: string | null): string {
  if (!iso) return 'Sin horario';
  return new Date(iso).toLocaleString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
